import { Box, Typography, useTheme } from '@mui/material';
import { ArrowUpRight } from 'lucide-react';
import React, { useMemo } from 'react';
import { resolverDeepLink } from './deepLinks';
import { parseMarkdown } from './markdown';

/**
 * Renderiza a resposta do assistente a partir do markdown simples.
 *
 * SEGURANÇA: tudo é elemento React com texto como filho (escapado pelo React);
 * não há `dangerouslySetInnerHTML`. Links só viram clicáveis se passarem por
 * `resolverDeepLink` (rotas internas conhecidas) e se o usuário tiver a
 * permissão da tela; caso contrário aparece só o rótulo, como texto.
 */

/** Chip de deep link (também usado pelos chips de follow-up). */
export function DeepLinkChip({ link, onNavigate, children, inline = false }) {
  const theme = useTheme();
  return (
    <Box
      component="a"
      href={link.path}
      onClick={(e) => {
        // Permite abrir em nova aba (ctrl/cmd/meio); clique simples navega no SPA.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        onNavigate(link.path);
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        px: inline ? '9px' : '11px',
        py: inline ? '1px' : '6px',
        borderRadius: '20px',
        fontSize: inline ? 'inherit' : 12,
        fontWeight: 600,
        lineHeight: inline ? 1.5 : 1.4,
        backgroundColor: 'info.bg',
        color: 'primary.main',
        textDecoration: 'none',
        border: '1px solid transparent',
        cursor: 'pointer',
        transition: 'border-color .15s ease',
        '&:hover': { borderColor: theme.palette.primary.main },
        '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
      }}
    >
      {children}
      <ArrowUpRight size={13} aria-hidden="true" />
    </Box>
  );
}

function Inlines({ tokens, onNavigate, canOpen }) {
  const theme = useTheme();
  return tokens.map((tk, i) => {
    switch (tk.t) {
      case 'bold':
        return <Box component="strong" key={i} sx={{ fontWeight: 700 }}>{tk.v}</Box>;
      case 'code':
        return (
          <Box
            component="code"
            key={i}
            sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12.5px', px: '5px', py: '1px', borderRadius: '6px', backgroundColor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}
          >
            {tk.v}
          </Box>
        );
      case 'link': {
        const link = resolverDeepLink(tk.href);
        if (link && canOpen(link)) {
          return <DeepLinkChip key={i} link={link} onNavigate={onNavigate} inline>{tk.label}</DeepLinkChip>;
        }
        return <React.Fragment key={i}>{tk.label}</React.Fragment>;
      }
      default:
        return <React.Fragment key={i}>{tk.v}</React.Fragment>;
    }
  });
}

function Tabela({ bloco, onNavigate, canOpen }) {
  const theme = useTheme();
  const alinhar = (c) => (bloco.numeric[c] ? 'right' : bloco.aligns[c] || 'left');
  return (
    <Box sx={{ my: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: '12px', overflow: 'auto', backgroundColor: 'background.paper' }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
        <thead>
          <tr>
            {bloco.head.map((h, c) => (
              <Box
                component="th"
                key={c}
                scope="col"
                sx={{ fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', fontWeight: 700, textAlign: alinhar(c), px: 1.5, py: 1, whiteSpace: 'nowrap', borderBottom: `1px solid ${theme.palette.divider}`, fontVariantNumeric: 'tabular-nums' }}
              >
                <Inlines tokens={h} onNavigate={onNavigate} canOpen={canOpen} />
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {bloco.rows.map((row, r) => (
            <Box component="tr" key={r} sx={{ '&:last-of-type td': { borderBottom: 'none' } }}>
              {row.map((cel, c) => (
                <Box
                  component="td"
                  key={c}
                  sx={{ px: 1.5, py: 1, whiteSpace: 'nowrap', textAlign: alinhar(c), fontWeight: c === 0 ? 700 : 400, borderBottom: `1px solid ${theme.palette.divider}`, fontVariantNumeric: 'tabular-nums' }}
                >
                  <Inlines tokens={cel} onNavigate={onNavigate} canOpen={canOpen} />
                </Box>
              ))}
            </Box>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

export default function MessageContent({ text, streaming = false, onNavigate, canOpen }) {
  const blocos = useMemo(() => parseMarkdown(text), [text]);
  const pode = canOpen || (() => true);

  return (
    <Box sx={{ fontSize: '13.5px', lineHeight: 1.55, wordBreak: 'break-word', minWidth: 0 }}>
      {blocos.map((b, i) => {
        switch (b.type) {
          case 'table':
            return <Tabela key={i} bloco={b} onNavigate={onNavigate} canOpen={pode} />;
          case 'ul':
          case 'ol':
            return (
              <Box key={i} component={b.type} sx={{ m: '0 0 8px', pl: 2.5 }}>
                {b.items.map((it, j) => (
                  <li key={j}><Inlines tokens={it} onNavigate={onNavigate} canOpen={pode} /></li>
                ))}
              </Box>
            );
          case 'h':
            return (
              <Typography key={i} component="p" sx={{ m: '0 0 8px', fontWeight: 800, fontSize: '14px' }}>
                <Inlines tokens={b.inlines} onNavigate={onNavigate} canOpen={pode} />
              </Typography>
            );
          default:
            return (
              <Box key={i} component="p" sx={{ m: '0 0 8px', '&:last-child': { mb: 0 } }}>
                <Inlines tokens={b.inlines} onNavigate={onNavigate} canOpen={pode} />
              </Box>
            );
        }
      })}
      {streaming && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            display: 'inline-block', width: 7, height: 14, ml: '2px', verticalAlign: '-2px', borderRadius: '1px', backgroundColor: 'primary.main',
            animation: 'aiCaret 1s steps(1) infinite',
            '@keyframes aiCaret': { '50%': { opacity: 0 } },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
      )}
    </Box>
  );
}
